// Reading preferences: per device, chosen by the child or a parent, applied as data
// attributes on <html> that tokens.css reacts to. No personal data: these are display
// settings, stored in localStorage when it is available and ignored when it is not.

export type ThemePref = "system" | "light" | "dark";
export type ContrastPref = "system" | "more" | "standard";
export type MotionPref = "system" | "reduce" | "full";
export type ReadingFont = "standard" | "dyslexia" | "opendyslexic";
export type TextSize = "standard" | "large";

export interface Preferences {
  readonly theme: ThemePref;
  readonly contrast: ContrastPref;
  readonly motion: MotionPref;
  readonly font: ReadingFont;
  readonly textSize: TextSize;
}

export const defaultPreferences: Preferences = {
  theme: "system",
  contrast: "system",
  motion: "system",
  font: "standard",
  textSize: "standard",
};

const STORAGE_KEY = "tutor.ui.preferences";

const allowed = {
  theme: ["system", "light", "dark"],
  contrast: ["system", "more", "standard"],
  motion: ["system", "reduce", "full"],
  font: ["standard", "dyslexia", "opendyslexic"],
  textSize: ["standard", "large"],
} as const satisfies { readonly [K in keyof Preferences]: readonly Preferences[K][] };

function pick<K extends keyof Preferences>(key: K, value: unknown): Preferences[K] {
  const options: readonly string[] = allowed[key];
  return typeof value === "string" && options.includes(value)
    ? (value as Preferences[K])
    : defaultPreferences[key];
}

/** Parses stored preferences; unknown or missing values fall back to the defaults. */
export function parsePreferences(raw: unknown): Preferences {
  const record: Readonly<Record<string, unknown>> =
    typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {};
  return {
    theme: pick("theme", record["theme"]),
    contrast: pick("contrast", record["contrast"]),
    motion: pick("motion", record["motion"]),
    font: pick("font", record["font"]),
    textSize: pick("textSize", record["textSize"]),
  };
}

/** Sets the data attributes tokens.css reads. "system" removes the attribute. */
export function applyPreferences(prefs: Preferences, root: HTMLElement): void {
  const set = (name: string, value: string | null) => {
    if (value === null) root.removeAttribute(name);
    else root.setAttribute(name, value);
  };
  set("data-theme", prefs.theme === "system" ? null : prefs.theme);
  set("data-contrast", prefs.contrast === "system" ? null : prefs.contrast);
  set("data-motion", prefs.motion === "system" ? null : prefs.motion);
  set("data-reading", prefs.font === "standard" ? null : prefs.font);
  set("data-text", prefs.textSize === "standard" ? null : prefs.textSize);
}

export function loadPreferences(storage: Pick<Storage, "getItem"> | undefined): Preferences {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    return parsePreferences(raw === null || raw === undefined ? null : JSON.parse(raw));
  } catch {
    return defaultPreferences;
  }
}

export function savePreferences(
  storage: Pick<Storage, "setItem"> | undefined,
  prefs: Preferences,
): void {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Private mode or storage blocked: the preference lasts for this visit only.
  }
}
